import type { Category, Todo } from "../../domain/entities.ts";
import { boardCaptureTags, nextBoardTaskPosition } from "./board-capture.ts";
import { applyColumnDefaults } from "./board-configuration.ts";
import { activeTags } from "./selectors.ts";

export interface InlineBoardTaskCapabilities {
  activeRoot(): string | null;
  editable(): boolean;
  categories(): Category[];
  tasks(): Todo[];
  emptyDraft(): Todo;
  create(draft: Todo, category: Category): Promise<Todo>;
  nextOccurrence(todo: Todo): Promise<unknown>;
  openEditor(todo: Todo): void;
  closeCapture(): void;
  render(): Promise<void>;
  wipExceeded(todo: Todo): boolean;
  markLocal(): void;
  sync(): void;
  toast(message: string, error?: boolean): void;
  today(): string;
  addDays(date: string, days: number): string;
}

function setFormDisabled(form: HTMLFormElement, disabled: boolean): void {
  Array.from(form.elements).forEach(control => { if (control instanceof HTMLInputElement || control instanceof HTMLButtonElement) control.disabled = disabled; });
}

export async function createInlineBoardTask(form: HTMLFormElement, openEditor: boolean, capabilities: InlineBoardTaskCapabilities): Promise<void> {
  const rootId = capabilities.activeRoot();
  if (!rootId || !capabilities.editable() || form.dataset.submitting === "true") return;
  const titleInput = form.elements.namedItem("title") as HTMLInputElement;
  const title = titleInput.value.trim();
  if (!title) { titleInput.focus(); titleInput.reportValidity(); return; }
  const collectionId = form.dataset.collectionId!;
  const category = capabilities.categories().find(item => item.id === collectionId);
  if (!category) { capabilities.toast("That collection is no longer available", true); return; }
  form.dataset.submitting = "true"; setFormDisabled(form, true);
  try {
    const boardTag = form.dataset.boardTag || null;
    const draft = capabilities.emptyDraft();
    if (form.hasAttribute("data-epic-id")) draft.epicId = form.dataset.epicId || null;
    const tags = boardCaptureTags(activeTags(draft), category.boardColumns || [], boardTag);
    draft.title = title; draft.categoryId = collectionId; draft.tags = Object.fromEntries(tags.map(tag => [tag, true])); draft.tagOrder = tags;
    if (boardTag) applyColumnDefaults(draft, category.boardColumnSettings?.[boardTag], days => capabilities.addDays(capabilities.today(), days), !category.approvalRule?.reviewerIds.length || !!category.approvalRule.column && category.approvalRule.column !== boardTag);
    draft.position = nextBoardTaskPosition(capabilities.tasks().filter(todo => todo.categoryId === collectionId && (activeTags(todo)[0] || null) === boardTag).map(todo => todo.position));
    const created = await capabilities.create(draft, category);
    if (created.completed && created.recurrence) await capabilities.nextOccurrence(created);
    capabilities.markLocal();
    if (openEditor) { capabilities.closeCapture(); capabilities.openEditor(created); }
    await capabilities.render();
    if (openEditor) document.querySelector<HTMLInputElement>("#detail-form input[name=title]")?.focus();
    else document.querySelector<HTMLInputElement>("[data-board-capture] input[name=title]")?.focus();
    const exceeded = capabilities.wipExceeded(created);
    capabilities.toast(`${openEditor ? "Task created · editing details" : "Task added"}${exceeded ? " · WIP limit exceeded" : ""}`, exceeded);
    capabilities.sync();
  } catch {
    delete form.dataset.submitting; setFormDisabled(form, false); titleInput.focus(); capabilities.toast("Could not add the task", true);
  }
}
